<template>
  <div>
    <MainLoading v-if="isLoading" />
    <MainError
      v-else-if="isError"
      title="Não foi possivel carregar o destaque da bolsa"
      description="Aguarde alguns minutos e tente novamente."
      @on-retry="handleOnRetry"
    />
    <div v-else class="market-today">
      <Heading :level="3" class="market-today--heading">
        {{ marketData.title }}
      </Heading>
      <FiltersList
        :is-loading="isLoading"
        class="market-today--filter"
        :filters="marketData.filters"
        @select-filter="handleOnSelectFilter"
      />
      <div class="market-today__subtitle">
        <BaseText :color="NebraskaColors.textSecondary" size="md">
          {{ marketData.subTitle }}
        </BaseText>
        <BaseText
          :color="NebraskaColors.textSecondary"
          size="xs"
          class="market-today__subtitle--date"
        >
          Última atualização: {{ marketData.date }}
        </BaseText>
      </div>
      <Grid
        v-if="marketData.items"
        :columns="[5, 5]"
        :gutter="32"
        class="market-today--row"
      >
        <InstrumentRow
          v-for="instrument in marketData.items"
          :key="instrument.symbol"
          :instrument="instrument"
          has-ellipsis
          @click.native="onInstrumentClick(instrument)"
        />
      </Grid>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component } from 'vue-property-decorator';

import { Heading, BaseText, Grid } from '@warrenbrasil/nebraska-web';
import { FiltersList } from '@/modules/trade/components/FiltersList';
import { InstrumentRow } from '@/modules/trade/components/InstrumentRow';
import { MainError } from '@/modules/trade/components/MainError';
import { MainLoading } from '@/modules/trade/components/MainLoading';

import {
  getMarketToday,
  IMarketToday,
  marketItems,
  MarketTodayFilters
} from '@/modules/trade/services/market-today';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { MarketTodayFilter } from './types';
import {
  trackMarketTodayClick,
  trackMarketTodayFilter,
  trackMarketHighlightsRequestError,
  trackMarketHighlightsRetryClick
} from '@/modules/trade/views/TradeView/trackers';
import { logError } from '@/modules/trade/helpers/logs';

@Component({
  components: {
    Heading,
    BaseText,
    Grid,
    InstrumentRow,
    FiltersList,
    MainLoading,
    MainError
  }
})
export default class MarketToday extends Vue {
  marketData!: IMarketToday;
  isLoading = true;
  isError = false;
  filter: MarketTodayFilters = MarketTodayFilters.HIGH_VOLUME;
  readonly NebraskaColors = NebraskaColors;

  created() {
    this.fetchMarketToday();
  }

  onInstrumentClick(instrument: marketItems) {
    trackMarketTodayClick(
      instrument.symbol,
      instrument.name,
      instrument.type.value
    );
    this.$router.push({
      name: 'tradeInstrument',
      params: { ticker: instrument.symbol }
    });
  }

  async handleOnRetry() {
    trackMarketHighlightsRetryClick();
    await this.fetchMarketToday();
  }

  private async fetchMarketToday() {
    this.isLoading = true;
    this.isError = false;
    getMarketToday(this.filter)
      .then((data: IMarketToday) => {
        this.marketData = data;
        this.filter = data.filters.selected;
      })
      .catch(e => {
        logError(e, 'request_market_highlights_error');
        trackMarketHighlightsRequestError();
        this.isError = true;
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  handleOnSelectFilter(filter: MarketTodayFilter) {
    trackMarketTodayFilter(filter.value);
    this.filter = filter.value;
    this.fetchMarketToday();
  }
}
</script>

<style lang="less" scoped>
.market-today {
  margin-top: @size-spacing-x1200;

  &__subtitle {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .to-md({
        display: block;
    });

    &--date {
      .to-md({
          margin-top: @size-spacing-x400;
      });
    }
  }

  &--filter {
    margin-bottom: @size-spacing-x600;
  }

  &--row {
    margin-top: @size-spacing-x400;
  }
}
</style>
